import type { UiTypes } from "@various/constants";
import type { DisposeNodeRectSize, DisposeSubRectOption, DisposeMainRectOption } from "./types";

import setting from "./setting";

//* 获取滚动条尺寸
export const disposeScrollSize = () => {
    //* 创建一个隐藏的div，并设置其内容足够大以产生滚动条
    const outer = document.createElement("div");
    const inner = document.createElement("div");

    //* 设置外部div的样式
    outer.style.visibility = "hidden";
    outer.style.overflow = "scroll";
    outer.style.position = "absolute";
    outer.style.top = "-9999px";
    document.body.appendChild(outer);

    //* 设置内部div的样式，使其足够大以产生滚动条
    inner.style.width = "100%";
    outer.appendChild(inner);

    //* 获取滚动条的宽度（外部div的宽度减去内部div的宽度）
    const size = outer.offsetWidth - inner.offsetWidth;
    //* 处理页面可视区域宽度
    const result = { width: 0, height: 0 };
    if (document.body.scrollHeight > innerHeight) result.width = size;
    if (document.body.scrollWidth > innerWidth) {
        result.height = size;
    }

    //* 清理
    document.body.removeChild(outer);
    //* 返回数据
    return result;
};

//* 主轴的边界处理函数
export const disposeMainAxis = (size: DisposeNodeRectSize, option: DisposeMainRectOption) => {
    if (option.strategy == "Disable") return { offset: option.offset, direction: option.direction };
    if (option.orientation > 0) {
        const offset = Math.floor(option.offset - size.container - size.view - size.space * 2);
        if (option.offset + size.view >= option.max && offset > option.min) {
            return { offset: offset, direction: setting.DIRECTION[option.direction] as UiTypes.direction };
        }
    } else {
        if (option.offset <= option.min && option.offset + size.container + size.view * 2 + size.space * 2 < option.max) {
            return {
                offset: Math.floor(option.offset + size.container + size.view + size.space * 2),
                direction: setting.DIRECTION[option.direction] as UiTypes.direction,
            };
        }
    }

    return { offset: option.offset, direction: option.direction };
};

//* 副轴的边界处理函数
export const disposeSubAxis = (size: DisposeNodeRectSize, option: DisposeSubRectOption) => {
    //* 数据初始化
    const result = { offset: option.offset + size.space, align: option.align };
    const difference = size.view - size.container;

    //* 减小最小最大位置的判定范围
    option.max -= 40;
    option.min += 40;

    //* 方向处理
    switch (option.align) {
        case "center": {
            //* 计算初始位置
            const offset = Math.floor(result.offset - difference / 2);
            //* 检测右侧超出边界
            if (option.strategy != "Disable" && offset + size.view >= option.max) {
                //* 检测右对齐时, 左侧是否未超出边界
                if (option.strategy == "Perfect") {
                    result.offset = option.max - size.view;
                    if (result.offset < option.min) result.offset = option.min;
                    return result;
                } else if (result.offset - difference - size.space * 2 > option.min) {
                    result.offset = result.offset - difference - size.space * 2;
                    result.align = "end";
                    return result;
                }
            }

            //* 检测左侧超出边界
            if (option.strategy != "Disable" && offset <= option.min) {
                if (option.strategy == "Perfect") {
                    result.offset = option.min;
                    if (result.offset + size.view > option.max) result.offset = option.max - size.view;
                    return result;
                } else if (result.offset + size.view < option.max) {
                    result.align = "start";
                    return result;
                }
            }

            result.offset = offset;
            return result;
        }

        case "start": {
            //* 检测是否左对齐时右侧超出边界并且右对齐时左侧是否在边界内
            if (option.strategy != "Disable" && result.offset + size.view >= option.max) {
                if (option.strategy == "Perfect") {
                    result.offset = option.max - size.view;
                    if (result.offset < option.min) {
                        result.offset = option.min;
                    }
                } else if (result.offset - difference - size.space * 2 > option.min) {
                    result.offset = result.offset - difference - size.space * 2;
                    result.align = "end";
                }
            }

            return result;
        }

        case "end": {
            //* 计算初始位置
            const offset = result.offset - difference - size.space * 2;
            //* 检测是否右对齐时左侧超出边界并且左对齐时右侧是否在边界内
            if (option.strategy != "Disable" && offset <= option.min) {
                if (option.strategy == "Perfect") {
                    result.offset = option.min;
                    if (result.offset + size.view > option.max) result.offset = option.max - size.view;
                    return result;
                } else if (result.offset + size.view < option.max) {
                    result.offset = result.offset;
                    result.align = "start";
                    return result;
                }
            }

            result.offset = offset;
            return result;
        }
    }
};

//* 窗口三角形属性设置
export const setTriangleAttr = (node: HTMLElement, direction: UiTypes.direction, align: UiTypes.align) => {
    //* 获取三角形属性
    const attrs: { [name: string]: string } = setting.TRIGGER[`${direction}_${align}`];
    //* 遍历设置三角形属性
    for (const i in attrs) {
        node.style[i as any] = attrs[i];
    }
};
